const STORAGE_KEY = 'admin_auth_token';

const BASE64_REGEX = /^[A-Za-z0-9+/_-]+=*$/;

interface JWTPayload {
  exp: number;
  sub: string;
  email?: string;
  roles?: string[];
}

function isJWTPayload(value: unknown): value is JWTPayload {
  if (typeof value !== 'object' || value === null) return false;
  return (
    'exp' in value &&
    typeof value.exp === 'number' &&
    'sub' in value &&
    typeof value.sub === 'string'
  );
}

function isValidBase64(str: string): boolean {
  return BASE64_REGEX.test(str);
}

export const tokenManager = {
  setToken: (token: string): void => {
    localStorage.setItem(STORAGE_KEY, token);
  },

  getToken: (): string | null => {
    return localStorage.getItem(STORAGE_KEY);
  },

  getPayload: (): JWTPayload | null => {
    const token = tokenManager.getToken();
    if (!token) return null;

    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        tokenManager.clearToken();
        return null;
      }

      const [header, payload, signature] = parts;

      if (!header || !payload || !signature) {
        tokenManager.clearToken();
        return null;
      }

      if (!isValidBase64(header) || !isValidBase64(payload) || !isValidBase64(signature)) {
        tokenManager.clearToken();
        return null;
      }

      const decoded: unknown = JSON.parse(atob(payload));

      if (!isJWTPayload(decoded)) {
        tokenManager.clearToken();
        return null;
      }

      return decoded;
    } catch {
      tokenManager.clearToken();
      return null;
    }
  },

  isTokenExpired: (): boolean => {
    const payload = tokenManager.getPayload();
    if (!payload) return true;

    const bufferSeconds = 60;
    const expirationMs = payload.exp * 1000;
    const bufferMs = bufferSeconds * 1000;

    return Date.now() >= expirationMs - bufferMs;
  },

  isAuthenticated: (): boolean => {
    return tokenManager.getToken() !== null && !tokenManager.isTokenExpired();
  },

  clearToken: (): void => {
    localStorage.removeItem(STORAGE_KEY);
  },

  getUserEmail: (): string | null => {
    const payload = tokenManager.getPayload();
    return payload?.email ?? null;
  },
};
